import { Injectable } from '@angular/core';
import { CameraEvent, LockEvent } from 'app/models/report.model';
import { IReportCSVRow } from 'app/models/report-csv.model';
import { HelperService } from './helper.service';
import { TranslateService } from '@ngx-translate/core';
import {
  ALERT_EXPORT_HEADERS,
  CAMERA_EXPORT_HEADERS,
  LOCK_EXPORT_HEADERS,
} from './report-export.constants';

export interface ReportExportTable {
  headers: readonly string[];
  rows: string[][];
}

@Injectable({
  providedIn: 'root',
})
export class ReportExportRowsService {
  constructor(
    private helper: HelperService,
    private translate: TranslateService,
  ) {}

  getAlertExportTable(alerts: any[]): ReportExportTable {
    const csvRows = this.getAlertCsvRows(alerts);
    return {
      headers: ALERT_EXPORT_HEADERS,
      rows: csvRows.map((row) => this.alertRowToCells(row)),
    };
  }

  getCameraVisitExportTable(events: CameraEvent[]): ReportExportTable {
    const csvRows = this.getCameraVisitCsvRows(events);
    return {
      headers: CAMERA_EXPORT_HEADERS,
      rows: csvRows.map((row) => this.cameraRowToCells(row)),
    };
  }

  getLockEventExportTable(events: LockEvent[]): ReportExportTable {
    const csvRows = this.getLockEventCsvRows(events);
    return {
      headers: LOCK_EXPORT_HEADERS,
      rows: csvRows.map((row) => this.lockRowToCells(row)),
    };
  }

  getAlertCsvRows(alerts: any[]): IReportCSVRow[] {
    const csvData: IReportCSVRow[] = [];

    alerts.forEach((row) => {
      const pressCount = Array.isArray(row.additionalPresses) ? row.additionalPresses.length : 0;
      csvData.push({
        CreateTime: this.helper.formatDate(row.receiveTime, 'l LTS'),
        Device: row.deviceName,
        EventType: this.helper.getActivationName(
          row.activationGroup,
          row.activationNode,
          row.deviceType,
          row.customActivationName,
          row.connTimeLimit
        ),
        Recipient: row.resolverName,
        TransferTime: this.helper.formatDate(row.completeTime, 'l LTS'),
        AdditionalPresses: pressCount,
      });
    });

    return csvData;
  }

  getCameraVisitCsvRows(events: CameraEvent[]): IReportCSVRow[] {
    const csvData: IReportCSVRow[] = [];

    events.forEach((row) => {
      csvData.push({
        CreateTime: this.helper.formatDate(row.visitStarted, 'l LTS'),
        Device: row.camera.displayName,
        EventType: row.camera.visitType,
        Recipient: row.userName || this.translate.instant('MISSED_CAMERA_VISIT'),
        TransferTime: this.helper.formatDate(row.visitEnd, 'l LTS'),
      });
    });

    return csvData;
  }

  getLockEventCsvRows(events: LockEvent[]): IReportCSVRow[] {
    const csvData: IReportCSVRow[] = [];

    events.forEach((row) => {
      csvData.push({
        CreateTime: this.helper.formatDate(String(row.createdTime), 'l LTS'),
        Device: row.lockName,
        EventType: row.eventType,
        Recipient: row.userName,
      });
    });

    return csvData;
  }

  private alertRowToCells(row: IReportCSVRow): string[] {
    return [
      String(row.CreateTime ?? ''),
      String(row.Device ?? ''),
      String(row.EventType ?? ''),
      String(row.Recipient ?? ''),
      String(row.TransferTime ?? ''),
      String(row.AdditionalPresses ?? ''),
    ];
  }

  private cameraRowToCells(row: IReportCSVRow): string[] {
    return [
      String(row.CreateTime ?? ''),
      String(row.Device ?? ''),
      String(row.EventType ?? ''),
      String(row.Recipient ?? ''),
      String(row.TransferTime ?? ''),
    ];
  }

  private lockRowToCells(row: IReportCSVRow): string[] {
    return [
      String(row.CreateTime ?? ''),
      String(row.Device ?? ''),
      String(row.EventType ?? ''),
      String(row.Recipient ?? ''),
    ];
  }
}
